/**
 * Petit constructeur DOM sans HTML brut : tout texte passe par `textContent`, donc rien n'est interprété comme du HTML.
 * Les styles n'utilisent que les variables du thème existant (`--teal`, `--warning`, `--danger`, `--success`…).
 */
export type DomChild = Node | string | null | false | undefined;

export interface DomProps {
  id?: string;
  class?: string;
  style?: string;
  type?: 'button' | 'checkbox';
  attrs?: Readonly<Record<string, string>>;
  disabled?: boolean;
  hidden?: boolean;
}

export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: DomProps = {},
  ...children: DomChild[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (props.id) node.id = props.id;
  if (props.class) node.className = props.class;
  if (props.style) node.setAttribute('style', props.style);
  if (props.type && node instanceof HTMLButtonElement) node.type = 'button';
  if (props.type === 'checkbox' && node instanceof HTMLInputElement) node.type = 'checkbox';
  if (props.disabled && (node instanceof HTMLButtonElement || node instanceof HTMLInputElement)) node.disabled = true;
  if (props.hidden) node.hidden = true;
  for (const [name, value] of Object.entries(props.attrs ?? {})) node.setAttribute(name, value);
  for (const child of children) {
    if (child === null || child === false || child === undefined) continue;
    node.append(typeof child === 'string' ? document.createTextNode(child) : child);
  }
  return node;
}

/** Remplace le contenu d'une zone et rend le focus au même élément (repéré par `data-focus-key`) s'il l'avait. */
export function replaceRegion(region: HTMLElement, ...children: DomChild[]): void {
  const active = document.activeElement;
  const key = active instanceof HTMLElement && region.contains(active) ? active.dataset['focusKey'] : undefined;
  region.replaceChildren(...children.filter((c): c is Node | string => c !== null && c !== false && c !== undefined));
  if (key) {
    const target = Array.from(region.querySelectorAll<HTMLElement>('[data-focus-key]')).find((el) => el.dataset['focusKey'] === key);
    target?.focus();
  }
}

export function prefersReducedMotion(): boolean {
  try {
    return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}
